'use client';

import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Button, Gleam, IconButton } from '@zillow/constellation-10';
import { SlidersHorizontal } from 'lucide-react';
import { LucideIcon } from '@/src/components/LucideIcon';

type AllFiltersButtonProps = {
  size: 'sm' | 'lg';
  compact?: boolean;
  activeCount: number;
  onClick: () => void;
};

/**
 * Trigger for the AllFiltersModal — a Constellation action wrapped in a
 * `Gleam` notification badge when filters are active.
 *
 * The SlidersHorizontal icon is always rendered. Gleam wraps the entire
 * IconButton (not the inner icon slot) so its absolute-positioned badge
 * lands at the top corner of the button instead of competing with the
 * icon's flex layout. The badge uses Gleam's `info` tone for the aqua
 * notification look (the default tone is alert-red).
 *
 * The accessible label includes the active count when filters are applied.
 */
export default function AllFiltersButton({ size, compact = false, activeCount, onClick }: AllFiltersButtonProps) {
  const tFilters = useTranslations('filters');
  const [isInteractionReady, setIsInteractionReady] = useState(false);
  const showCount = activeCount > 0;

  useEffect(() => {
    setIsInteractionReady(true);
  }, []);

  const iconButtonLabel = showCount ? tFilters('allFiltersActive', { count: activeCount }) : tFilters('allFilters');
  const visibleButtonLabel = tFilters('filtersLabel');
  const visibleButtonAccessibleLabel = showCount ? tFilters('filtersActive', { count: activeCount }) : undefined;

  const button =
    size === 'sm' && !compact ? (
      <Button
        data-testid="button-all-filters"
        type="button"
        aria-label={visibleButtonAccessibleLabel}
        size="sm"
        tone="neutral"
        emphasis="bare"
        css={{ borderRadius: 'pill' }}
        disabled={!isInteractionReady}
        onClick={onClick}
        icon={<LucideIcon icon={SlidersHorizontal} />}
      >
        {visibleButtonLabel}
      </Button>
    ) : (
      <IconButton
        data-testid="button-all-filters"
        type="button"
        title={iconButtonLabel}
        shape="circle"
        size={size}
        tone="neutral"
        emphasis="bare"
        disabled={!isInteractionReady}
        onClick={onClick}
        icon={<LucideIcon icon={SlidersHorizontal} />}
      />
    );

  if (!showCount) return button;

  return (
    <Gleam count={activeCount} tone="info">
      {button}
    </Gleam>
  );
}
